import {assert} from 'Asserts';
import {TagSchema} from 'constants/RegistryConstants';
import {STORABLE_SCHEMA_TYPE_TO_STORABLE_TYPE} from 'constants/SampleConstants';
import {BenchlingTypeDef, LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {Union} from 'data-types/benchling-type-def/ComplexTypes';
import {
  getItemTypeDefForSchema,
  getTypeDefFromSchemaOrItemType,
} from 'data-types/benchling-type-def/ModelToTypeDef';
import {TagSchemaSupportedFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FIELD_TYPE_TO_LEAF_TYPE,
  getRequiredLinkFolderItemType,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import APIUtil from 'util/APIUtil';

import {
  COLLAPSING_LOOKUP_STEP_TYPES,
  LookupConfigSpec,
  LookupStepSpec,
  LookupStepType,
  MODIFIER_LOOKUP_STEP_TYPES,
  NotebookColumnItemType,
  SchemaFieldLookupStepSpec,
} from './LookupConfigModalConstants';
import {getLinkSchemaForLookupStep} from './LookupConfigUtils';

// Keep all logic in this file in sync with benchling/lookup_steps/infer_from_lookup_config.py

export const MISSING_LOOKUP_SCHEMA_ERROR_MESSAGE =
  'Expected a schema to be defined to look up a schema field.';

export class LookupSchemaNotFoundError extends Error {}
export class LookupSchemaFieldNotFoundError extends Error {}

interface BaseInferredType {
  typeDef: BenchlingTypeDef;
  linkSelectorId?: string;
  linkSchema?: Schema | null;
}

export type InferredLookupConfigType = BaseInferredType & {isMultiplex: boolean; isSnapshot: boolean};
export type InferredLookupStepType = BaseInferredType & {isStepMultiplex: boolean; isStepSnapshot: boolean};

function getLookupStepIdxToInferFrom(lookupSteps: Array<LookupStepSpec>): number {
  assert(lookupSteps.length > 0, 'Expected at least one lookup step');
  const lastStep = lookupSteps[lookupSteps.length - 1];
  if (MODIFIER_LOOKUP_STEP_TYPES.includes(lastStep.type)) {
    return getLookupStepIdxToInferFrom(lookupSteps.slice(0, -1));
  }
  return lookupSteps.length - 1;
}

function getSchemaFieldType(
  lookupStep: SchemaFieldLookupStepSpec,
  previousSchema: Schema,
  linkSchema: Schema | null
): InferredLookupStepType {
  const field: SchemaField | undefined = previousSchema.allFields.find(
    ({apiId, systemName}) => apiId === lookupStep.schemaField || systemName === lookupStep.schemaField
  );
  if (field == null) {
    throw new LookupSchemaFieldNotFoundError(
      `Expected to find the schema field ${lookupStep.schemaField} in ${previousSchema.name}`
    );
  }
  const folderItemType = getRequiredLinkFolderItemType(field) as TagSchemaSupportedFolderItemType;
  const typeDef = getTypeDefFromSchemaOrItemType(
    linkSchema ?? null,
    folderItemType,
    FIELD_TYPE_TO_LEAF_TYPE[field.fieldType]
  );
  return {
    typeDef,
    linkSchema,
    linkSelectorId: field.schemaFieldSelectorId ?? undefined,
    isStepMultiplex: field.isMulti,
    isStepSnapshot: field.isSnapshot,
  };
}

export function inferTypeFromNonModifierStep(
  lookupStep: LookupStepSpec,
  previousSchema: Schema | null,
  linkSchema: Schema | null
): InferredLookupStepType {
  switch (lookupStep.type) {
    case LookupStepType.SCHEMA_FIELD: {
      if (previousSchema == null) {
        throw new LookupSchemaNotFoundError(MISSING_LOOKUP_SCHEMA_ERROR_MESSAGE);
      }
      return getSchemaFieldType(lookupStep, previousSchema, linkSchema);
    }
    case LookupStepType.ALIASES:
      return {typeDef: LeafType.TEXT, isStepMultiplex: true, isStepSnapshot: false};
    case LookupStepType.CHEMICAL_STRUCTURE:
      return {typeDef: LeafType.BLOB, isStepMultiplex: false, isStepSnapshot: false};
    case LookupStepType.AVAILABLE_SPACE:
    case LookupStepType.BARCODE:
    case LookupStepType.LOCATION_PATH:
    case LookupStepType.NAME:
    case LookupStepType.REGISTRY_IDENTIFIER:
    case LookupStepType.RESIDUES:
    case LookupStepType.WELL_COORDINATES:
      return {typeDef: LeafType.TEXT, isStepMultiplex: false, isStepSnapshot: false};
    case LookupStepType.QUANTITY:
    case LookupStepType.CONCENTRATION:
      return {typeDef: LeafType.DECIMAL, isStepMultiplex: false, isStepSnapshot: false};
    case LookupStepType.COUNT:
    case LookupStepType.FREEZE_THAW_COUNT:
      return {typeDef: LeafType.INTEGER, isStepMultiplex: false, isStepSnapshot: false};
    case LookupStepType.BOX:
      return {
        typeDef: LeafType.BOX,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    case LookupStepType.BOX_CONTAINERS:
      return {
        typeDef: LeafType.CONTAINER,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    case LookupStepType.CHILD_ENTITIES: {
      const typeDef = getTypeDefFromSchemaOrItemType(linkSchema, null, LeafType.BIOENTITY);
      return {
        typeDef,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.PLATE:
      return {
        typeDef: LeafType.PLATE,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    case LookupStepType.LOCATION:
      return {
        typeDef: LeafType.LOCATION,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    case LookupStepType.WELLS:
      return {
        typeDef: LeafType.CONTAINER,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    case LookupStepType.CONTAINER:
      return {
        typeDef: LeafType.CONTAINER,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    case LookupStepType.CONTAINER_BY_GRID_POSITION: {
      return {
        typeDef: LeafType.CONTAINER,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.CONTENTS: {
      const folderItemType = ((lookupStep.entitySchemaType ||
        (linkSchema as TagSchema | null)?.folderItemType) ??
        null) as TagSchemaSupportedFolderItemType | null;
      const typeDef = getTypeDefFromSchemaOrItemType(linkSchema ?? null, folderItemType, LeafType.BIOENTITY);
      return {
        typeDef,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.SCHEMA_IDENTITY: {
      const typeDef = linkSchema != null ? getItemTypeDefForSchema(linkSchema) : LeafType.BIOENTITY;
      return {typeDef, linkSchema, isStepMultiplex: false, isStepSnapshot: false};
    }
    case LookupStepType.RESULTS: {
      return {
        typeDef: LeafType.RESULT,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.FLOWCHART_PROCESS_TASK:
      return {
        typeDef: LeafType.PROCESS_TASK,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    case LookupStepType.PROCESS_TASK: {
      return {
        typeDef: LeafType.PROCESS_TASK,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.TASK_FINDER_PROCESS_TASKS: {
      return {
        typeDef: LeafType.PROCESS_TASK,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.CONSTANT: {
      const typeDef =
        typeof lookupStep.value === 'number'
          ? Number.isInteger(lookupStep.value)
            ? LeafType.INTEGER
            : LeafType.DECIMAL
          : LeafType.TEXT;
      return {
        typeDef,
        linkSchema: null,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    }
    case LookupStepType.NOTEBOOK_COLUMN_TYPECHECK: {
      const baseType = {linkSchema, isStepMultiplex: false, isStepSnapshot: false};
      if (lookupStep.notebookColumnItemType === NotebookColumnItemType.CONTAINER) {
        return {
          ...baseType,
          typeDef: LeafType.CONTAINER,
        };
      } else if (lookupStep.notebookColumnItemType === NotebookColumnItemType.PLATE) {
        return {
          ...baseType,
          typeDef: LeafType.PLATE,
        };
      } else if (lookupStep.notebookColumnItemType === NotebookColumnItemType.BOX) {
        return {
          ...baseType,
          typeDef: LeafType.BOX,
        };
      } else if (lookupStep.notebookColumnItemType === NotebookColumnItemType.LOCATION) {
        return {
          ...baseType,
          typeDef: LeafType.LOCATION,
        };
      } else if (lookupStep.notebookColumnItemType === NotebookColumnItemType.STORABLE) {
        const storableType =
          linkSchema != null && APIUtil.isStorableSchema(linkSchema.id)
            ? STORABLE_SCHEMA_TYPE_TO_STORABLE_TYPE[APIUtil.apiIdToType(linkSchema.id)]
            : null;
        const leafType =
          (storableType != null && LeafType[storableType.toUpperCase() as keyof typeof LeafType]) ||
          LeafType.STORABLE;
        return {
          ...baseType,
          typeDef: leafType,
        };
      }
      assert(
        lookupStep.notebookColumnItemType === NotebookColumnItemType.ENTITY,
        `Unsupported notebookColumnItemType ${lookupStep.notebookColumnItemType}`
      );
      const folderItemType = ((linkSchema as TagSchema | null)?.folderItemType ??
        null) as TagSchemaSupportedFolderItemType | null;
      const typeDef = getTypeDefFromSchemaOrItemType(linkSchema ?? null, folderItemType, LeafType.BIOENTITY);
      return {
        ...baseType,
        typeDef,
      };
    }
    case LookupStepType.PLACEHOLDER_BOXES:
      return {
        typeDef: LeafType.BOX,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    case LookupStepType.PLACEHOLDER_PLATES:
      return {
        typeDef: LeafType.PLATE,
        linkSchema,
        isStepMultiplex: false,
        isStepSnapshot: false,
      };
    case LookupStepType.SAMPLE_OWNERS:
    case LookupStepType.AUTHORIZED_USERS:
      return {
        typeDef: new Union(new Set([LeafType.USER, LeafType.TEAM])),
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    case LookupStepType.COMPUTED_EXPIRATION_DATE:
      return {typeDef: LeafType.DATETIME, isStepMultiplex: false, isStepSnapshot: false};
    case LookupStepType.STUDIES:
      return {typeDef: LeafType.STUDY, isStepMultiplex: true, isStepSnapshot: false};
    case LookupStepType.TRANSLATION_LINKS_TO_ENTITY: {
      const typeDef = getTypeDefFromSchemaOrItemType(linkSchema, null, LeafType.SEQUENCE);
      return {
        typeDef,
        linkSchema,
        isStepMultiplex: true,
        isStepSnapshot: false,
      };
    }
    default:
      assert(false, `Unsupported step type ${lookupStep.type}.`);
  }
}

function getAllStepTypes(
  lookupConfig: LookupConfigSpec,
  baseSchema: Schema
): Array<InferredLookupStepType | null> {
  const inferredTypes = [];
  let currentType: InferredLookupStepType | null = null;
  for (const lookupStep of lookupConfig.lookupSteps) {
    if (MODIFIER_LOOKUP_STEP_TYPES.includes(lookupStep.type)) {
      inferredTypes.push(null);
    } else {
      const previousSchema = currentType != null ? (currentType.linkSchema ?? null) : baseSchema;
      currentType = inferTypeFromNonModifierStep(
        lookupStep,
        previousSchema,
        getLinkSchemaForLookupStep(lookupStep, previousSchema)
      );
      inferredTypes.push(currentType);
    }
  }

  assert(inferredTypes.length === lookupConfig.lookupSteps.length, 'Expected one inferred type per step');
  return inferredTypes;
}

interface LookupStepGroup {
  modifierSteps: Array<LookupStepSpec>;
  baseStepIdx: number;
}

function doesLookupStepGroupHaveMultiCardinality(
  {modifierSteps, baseStepIdx}: LookupStepGroup,
  allStepTypes: Array<InferredLookupStepType | null>
): boolean {
  const stepType = allStepTypes[baseStepIdx];
  if (!stepType!.isStepMultiplex) {
    return false;
  } else if (modifierSteps.some(({type}) => COLLAPSING_LOOKUP_STEP_TYPES.includes(type))) {
    return false;
  }
  return true;
}

function doesLookupConfigHaveMultiCardinality(
  lookupConfig: LookupConfigSpec,
  allStepTypes: Array<InferredLookupStepType | null>
): boolean {
  const lookupStepGroups: Array<LookupStepGroup> = [];
  for (let i = 0; i < lookupConfig.lookupSteps.length; i++) {
    const lookupStep = lookupConfig.lookupSteps[i];
    if (!MODIFIER_LOOKUP_STEP_TYPES.includes(lookupStep.type)) {
      lookupStepGroups.push({
        modifierSteps: [],
        baseStepIdx: i,
      });
    } else {
      lookupStepGroups[lookupStepGroups.length - 1].modifierSteps.push(lookupStep);
    }
  }
  return lookupStepGroups.some((lookupStepGroup) =>
    doesLookupStepGroupHaveMultiCardinality(lookupStepGroup, allStepTypes)
  );
}

/**
 * Determines the type of the item(s) returned by the provided lookup config.
 *
 * @param lookupConfig The lookup config
 * @param baseSchema The schema the lookup config starts from
 * @returns The type of the item(s) looked up by the lookup config
 */
export default function inferTypeFromLookupConfig(
  lookupConfig: LookupConfigSpec,
  baseSchema: Schema
): InferredLookupConfigType {
  assert(lookupConfig.lookupSteps.length > 0, 'Expected at least one lookup step');
  const stepIdx = getLookupStepIdxToInferFrom(lookupConfig.lookupSteps);
  const allStepTypes = getAllStepTypes(lookupConfig, baseSchema);
  const lastStepType = allStepTypes[stepIdx];
  assert(lastStepType != null, 'Expected lookup step to have a non-modifier type');
  const isMultiplex = doesLookupConfigHaveMultiCardinality(lookupConfig, allStepTypes);
  return {
    typeDef: lastStepType.typeDef,
    linkSchema: lastStepType.linkSchema,
    ...(lastStepType.linkSelectorId != null ? {linkSelectorId: lastStepType.linkSelectorId} : {}),
    isMultiplex,
    isSnapshot: allStepTypes.some((step) => step && step.isStepSnapshot),
  };
}
